import { Star, MapPin, CalendarDays, Clock } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../lib/api';

export default function RescheduleCalendarPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { consultation } = location.state || {}; // appointment to reschedule
  const [doctor, setDoctor] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [loading, setLoading] = useState(false);

  const styles = {
    backArrow: {
      cursor: 'pointer',
      fontSize: '20px',
      color: 'var(--primary)',
    },
  };

  const timeOptions = [
    '08:00 AM', '09:00 AM', '10:00 AM', '11:00 AM',
    '12:00 PM', '01:00 PM', '02:00 PM', '03:00 PM',
  ];

  // Fetch doctor info
  useEffect(() => {
    async function fetchDoctor() {
      if (!consultation?.doctor) return;
      try {
        const res = await api.get(`/api/doctor/${consultation.doctor._id}`);
        setDoctor(res.data);
      } catch (err) {
        console.error('Failed to fetch doctor:', err);
      }
    }
    fetchDoctor();
  }, [consultation]);

  const handleReschedule = async () => {
    if (!selectedDate || !selectedTime) return;
    setLoading(true);

    // combine date and time to a proper ISO string
    const [hour, minutePart] = selectedTime.split(/[: ]/);
    const minute = minutePart === 'AM' || minutePart === 'PM' ? 0 : parseInt(minutePart, 10);
    let hour24 = parseInt(hour, 10);
    if (selectedTime.includes('PM') && hour24 !== 12) hour24 += 12;
    if (selectedTime.includes('AM') && hour24 === 12) hour24 = 0;
    const [year, month, day] = selectedDate.split('-');
    const appointmentTime = new Date(year, month - 1, day, hour24, minute).toISOString();

    try {
      await api.put(`/api/consultations/${consultation._id}/reschedule`, {
        appointmentTime,
      });
      alert('Appointment rescheduled successfully!');
      navigate(-1); // go back to timeline
    } catch (err) {
      console.error('Failed to reschedule:', err);
      alert('Failed to reschedule appointment.');
    } finally {
      setLoading(false);
    }
  };

  if (!consultation || !doctor) return <p>Loading...</p>;

  return (
    <div className="container py-4 px-3" style={{ maxWidth: 480 }}>
      {/* Header */}
      <div className="d-flex align-items-center mb-4">
        <button
          className="btn p-0 me-3"
          onClick={() => navigate(-1)}
          style={{ border: 'none', background: 'none' }}
        >
          <i className="fi fi-br-angle-left" style={styles.backArrow}></i>
        </button>
        <h5 className="fw-semibold mb-0" style={{ color: 'var(--primary)' }}>Reschedule Appointment</h5>
      </div>

      {/* Doctor Card */}
      <div className="card border-0 shadow-sm rounded-4 mb-4 p-3" style={{ background: 'linear-gradient(135deg, rgba(127, 0, 255, 0.08), #ffffff)', border: '1px solid rgba(127, 0, 255, 0.14)' }}>
        <div className="d-flex align-items-center">
          <img
            src={doctor.photo || 'https://randomuser.me/api/portraits/men/45.jpg'}
            alt={doctor.name}
            className="rounded-circle me-3"
            style={{ width: 70, height: 70, objectFit: 'cover', border: '3px solid rgba(127, 0, 255, 0.28)' }}
          />
          <div>
            <h6 className="fw-semibold mb-1">{doctor.name}</h6>
            <p className="text-muted mb-1" style={{ fontSize: 13 }}>
              {doctor.specialty} • <Star size={13} className="text-warning me-1" />{doctor.rating}★ ({doctor.reviews || 0})
            </p>
            <p className="text-muted mb-0" style={{ fontSize: 13 }}>
              <MapPin size={13} className="me-1" />
              {doctor.distance || 'Unknown distance'}
            </p>
          </div>
        </div>
      </div>

      {/* Date Picker */}
      <h6 className="fw-semibold mb-2"><CalendarDays size={16} className="me-2" style={{ color: 'var(--primary)' }} />Choose a Date</h6>
      <div className="mb-4">
        <input
          type="date"
          className="form-control form-control-lg rounded-3 shadow-sm"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          style={{ fontSize: 15, padding: '10px 14px', borderColor: 'rgba(127, 0, 255, 0.22)' }}
        />
      </div>

      {/* Time Picker */}
      <h6 className="fw-semibold mb-2"><Clock size={16} className="me-2" style={{ color: 'var(--primary)' }} />Choose a Time</h6>
      <div className="d-flex flex-wrap gap-2 mb-4" style={{ overflowX: 'auto', scrollbarWidth: 'none' }}>
        {timeOptions.map((time, idx) => (
          <button
            key={idx}
            className={`btn rounded-pill px-3 py-2 fw-semibold ${selectedTime === time ? '' : 'btn-outline-secondary'}`}
            onClick={() => setSelectedTime(time)}
            style={{
              fontSize: 14,
              borderColor: selectedTime === time ? 'var(--primary)' : undefined,
              background: selectedTime === time ? 'var(--primary)' : undefined,
              color: selectedTime === time ? '#fff' : undefined,
            }}
          >
            {time}
          </button>
        ))}
      </div>

      {/* Selection Summary */}
      {selectedDate && selectedTime && (
        <div className="alert border rounded-4 shadow-sm mb-4 text-center" style={{ fontSize: 14, background: 'rgba(127, 0, 255, 0.06)', borderColor: 'rgba(127, 0, 255, 0.24)', color: 'var(--on-bg)' }}>
          You selected <strong>{new Date(selectedDate).toDateString()}</strong> at <strong>{selectedTime}</strong>
        </div>
      )}

      {/* Continue Button */}
      <button
        className="btn btn-primary w-100 py-3 fw-semibold rounded-4 shadow-sm"
        disabled={!selectedDate || !selectedTime || loading}
        onClick={handleReschedule}
        style={{
          fontSize: 16,
          opacity: !selectedDate || !selectedTime ? 0.7 : 1,
          backgroundColor: 'var(--primary)',
          borderColor: 'var(--primary)',
          color: '#fff',
          '--bs-btn-bg': 'var(--primary)',
          '--bs-btn-border-color': 'var(--primary)',
          '--bs-btn-hover-bg': 'var(--primary)',
          '--bs-btn-hover-border-color': 'var(--primary)',
          '--bs-btn-active-bg': 'var(--primary)',
          '--bs-btn-active-border-color': 'var(--primary)',
        }}
      >
        {loading ? 'Rescheduling...' : 'Reschedule Appointment'}
      </button>
    </div>
  );
}
